import React from 'react';
import Layout from '@/components/Layout';
import { GlassCard } from '@/components/ui/glass-card';
import { Download, FileText, Calendar, BookOpen, Shield } from 'lucide-react';

export default function ResourcesPage() {
  const downloads = [
    { icon: FileText, title: 'School Prospectus 2026-27', size: '2.5 MB', format: 'PDF' },
    { icon: FileText, title: 'Fee Schedule', size: '500 KB', format: 'PDF' },
    { icon: FileText, title: 'Admission Brochure', size: '1.8 MB', format: 'PDF' },
    { icon: Calendar, title: 'Academic Calendar 2026-27', size: '800 KB', format: 'PDF' },
    { icon: BookOpen, title: 'Syllabus - Class 1-5', size: '1.2 MB', format: 'PDF' },
    { icon: BookOpen, title: 'Syllabus - Class 6-10', size: '1.5 MB', format: 'PDF' },
    { icon: Shield, title: 'School Policies', size: '600 KB', format: 'PDF' },
    { icon: FileText, title: 'Parent Handbook', size: '1.0 MB', format: 'PDF' }
  ];

  const handbooks = [
    { title: 'Parent Handbook', desc: 'Complete guide for parents including policies, procedures, and important information' },
    { title: 'Student Handbook', desc: 'Essential information for students about school rules, expectations, and resources' },
    { title: 'Code of Conduct', desc: 'Expected behavior standards for all members of school community' },
    { title: 'Safety Guidelines', desc: 'Important safety protocols and emergency procedures' }
  ];

  return (
    <Layout>
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6">Resources</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Download important documents, policies, and handbooks
          </p>
        </div>
      </section>

      {/* Downloads */}
      <section className="py-20 bg-slate-50" id="downloads">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-blue-900 mb-12 text-center">Downloads</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {downloads.map((item, i) => (
              <GlassCard key={i} className="hover:shadow-2xl transition-shadow cursor-pointer">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-blue-900" />
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                    {item.format}
                  </span>
                </div>
                <h3 className="font-bold text-blue-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 mb-4">{item.size}</p>
                <button className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors">
                  <Download className="w-4 h-4" />
                  <span className="text-sm font-semibold">Download</span>
                </button>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Handbooks */}
      <section className="py-20 bg-white" id="handbooks">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-blue-900 mb-12 text-center">Handbooks & Policies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {handbooks.map((item, i) => (
              <GlassCard key={i} className="hover:shadow-2xl transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-blue-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 mb-4">{item.desc}</p>
                    <button className="flex items-center space-x-2 text-blue-900 font-semibold hover:text-blue-700 transition-colors">
                      <Download className="w-4 h-4" />
                      <span>Download PDF</span>
                    </button>
                  </div>
                  <FileText className="w-12 h-12 text-blue-200 flex-shrink-0 ml-4" />
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Calendar */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-100" id="calendar">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="w-16 h-16 text-blue-900 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-blue-900 mb-4">Academic Calendar 2026-27</h2>
          <p className="text-lg text-slate-600 mb-8">
            Stay updated with all important dates, holidays, and school events
          </p>
          <button className="inline-flex items-center space-x-3 px-8 py-4 bg-blue-900 text-white rounded-xl hover:bg-blue-800 transition-colors font-semibold">
            <Download className="w-5 h-5" />
            <span>Download Full Calendar</span>
          </button>
        </div>
      </section>
    </Layout>
  );
}
